import { Box } from "@outschool/backpack";
import { StandardHeaderLeft } from "@outschool/ui-components-website";
import { ConditionalTrackingPageSection } from "@outschool/ui-legacy-component-library";
import { lazyWithRetry, Screen, useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { useStickyHeader } from "../../hooks/useStickyHeader";
import { UserMode, useUserMode, useUserModeRootPath } from "../../lib/UserMode";
import { EducatorModeToolbar } from "./EducatorModeToolbar";

const DesktopHeaderNavigation = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "desktopHeaderNavigation" */ "./DesktopHeaderNav"
  ).then(module => ({
    default: module.DesktopHeaderNavigation,
  })),
);
const MobileHeaderNavigation = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "mobileHeaderNavigation" */ "./MobileHeaderNav"
    ),
);

const GlobalSearchBar = lazyWithRetry(() => import("./GlobalSearchBar"));

interface Props {
  trackingUniqueId?: string;
  showCategoriesNavbar?: boolean;
  showSearchBox?: boolean;
  hideHeaderRight?: boolean;
  sx?: any;
}

export const HEADER_WIDTH = 1440;

export default function Header({
  trackingUniqueId,
  showCategoriesNavbar = false,
  showSearchBox = true,
  hideHeaderRight = false,
  sx,
}: Props) {
  const userMode = useUserMode();
  const isEducatorMode = userMode === UserMode.Educator;

  return (
    <ConditionalTrackingPageSection
      name="header"
      uniqueId={trackingUniqueId ? trackingUniqueId : "header"}
    >
      {setImpressionNode => (
        <Box ref={setImpressionNode}>
          <StandardHeader
            hideFindClasses={isEducatorMode}
            showCategoriesNavbar={showCategoriesNavbar}
            showSearchBox={showSearchBox}
            rightChildren={hideHeaderRight ? null : undefined}
            sx={sx}
          />
        </Box>
      )}
    </ConditionalTrackingPageSection>
  );
}

function StandardHeaderRight({ hideFindClasses }: $TSFixMe) {
  const isMobile = Screen.useIsMobile();

  if (isMobile) {
    return (
      <React.Suspense fallback={null}>
        <MobileHeaderNavigation hideFindClasses={hideFindClasses} />
      </React.Suspense>
    );
  }

  return (
    <React.Suspense fallback={null}>
      <DesktopHeaderNavigation />
    </React.Suspense>
  );
}

const HeaderToolbar = ({ showSearchBox = true }) => {
  const isMobile = Screen.useIsMobile();
  const isEducatorMode = useUserMode() === UserMode.Educator;

  if (!isMobile) {
    if (isEducatorMode) {
      return <EducatorModeToolbar />;
    }
    return <GlobalSearchBar showSearchBox={showSearchBox} />;
  }
  return null;
};

interface StandardHeaderProps {
  sx?: any;
  hideFindClasses?: boolean;
  leftChildren?: any;
  rightChildren?: any;
  showCategoriesNavbar?: boolean;
  showSearchBox?: boolean;
}
export const StandardHeader = ({
  sx,
  hideFindClasses,
  leftChildren,
  rightChildren,
  showCategoriesNavbar,
  showSearchBox = true,
}: StandardHeaderProps) => {
  const ref = React.useRef<HTMLElement>(null);
  const rootPath = useUserModeRootPath();
  const { scrolled } = useStickyHeader();

  const isMobile = useIsMobile();

  if (leftChildren === undefined) {
    // if null, omit left header
    leftChildren = (
      <StandardHeaderLeft
        rootPath={rootPath}
        showCategoriesNavbar={isMobile ? true : showCategoriesNavbar}
      />
    );
  }
  if (rightChildren === undefined) {
    // if null, omit right header
    rightChildren = <StandardHeaderRight hideFindClasses={hideFindClasses} />;
  }
  return (
    <Box
      ref={ref}
      sx={{
        zIndex: 201,
        position: "relative",
        backgroundColor: "common.white",
        transition: "box-shadow 0.2s ease-in-out",
        ...(scrolled ? { boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.08)" } : {}),
        ...sx,
      }}
    >
      <Box
        flex
        sx={(theme: $TSFixMe) => ({
          gap: "2em",
          paddingY: 5,
          maxWidth: HEADER_WIDTH,
          height: "64px",
          justifyContent: "center",
          alignItems: "center",
          marginLeft: "auto",
          marginRight: "auto",
          [theme.breakpoints.down("md")]: {
            paddingY: 0,
            justifyContent: "space-between",
          },
          [theme.breakpoints.down("sm")]: {
            gap: "0.5em",
          },
          // Needs to be in sync with HEADER_WIDTH
          // probably doesn't 100% work with backpack's text size scaling?
          // TODO figure out the "right" way to do this
          "@media (max-width: 1440px)": {
            mx: 24,
          },
          [theme.breakpoints.between("md", "lg")]: {
            gap: "1em",
          },
        })}
      >
        {leftChildren}
        <HeaderToolbar showSearchBox={showSearchBox} />
        {rightChildren}
      </Box>
    </Box>
  );
};
